import React from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Clock, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { BlogPost } from '@/services/blog/types';

interface BlogHeaderProps {
  post: BlogPost;
  isAdmin: boolean;
  onEdit: () => void;
}

export const BlogHeader: React.FC<BlogHeaderProps> = ({ post, isAdmin, onEdit }) => {
  return (
    <div className="mb-8">
      <div className="flex justify-between items-start mb-4">
        <div className="space-y-1">
          <h1 className="text-4xl md:text-5xl font-bold text-left">{post.title}</h1>
        </div>
        
        {isAdmin && (
          <Button onClick={onEdit} variant="outline" size="sm" className="flex items-center gap-2">
            <Edit size={16} />
            Edit Post
          </Button>
        )}
      </div>
      
      {post.summary && (
        <p className="text-xl text-neutral-600 mt-4 mb-6 text-left">{post.summary}</p>
      )}
      
      <div className="flex items-center justify-between flex-wrap gap-4 text-sm text-neutral-500">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={post.author?.avatar_url || undefined} alt={post.author?.full_name || "Author"} />
            <AvatarFallback>{(post.author?.full_name?.[0] || post.author?.username?.[0] || 'A').toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-neutral-900">{post.author?.full_name || post.author?.username || 'Unknown'}</p>
            <p className="text-xs">{post.published_at ? format(new Date(post.published_at), 'MMM d, yyyy') : 'Recently published'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <Clock size={16} />
          <span>{post.reading_time_minutes || 1} min read</span>
        </div>
      </div>
    </div>
  );
};
